import { useCallback, useEffect, useRef, useState } from "react";

export function filterPreloadedDropDownOptions<Option extends { label?: unknown }>(
  options: Option[],
  inputValue: string,
): Option[] {
  const query = inputValue.trim().toLowerCase();
  if (!query) return options;
  return options.filter((option) =>
    String(option.label ?? "").toLowerCase().includes(query),
  );
}

interface UseDropDownOptionPreloadProps<Option> {
  disabled?: boolean;
  loadAllOptions?: () => Promise<Option[]>;
  resetKey?: unknown;
  fallbackDefaultOptions?: boolean | Option[];
  onLoadError?: (error: unknown) => void;
}

export function useDropDownOptionPreload<Option>({
  disabled = false,
  loadAllOptions,
  resetKey,
  fallbackDefaultOptions,
  onLoadError,
}: UseDropDownOptionPreloadProps<Option>) {
  const [preloadedOptions, setPreloadedOptions] = useState<Option[] | null>(
    null,
  );
  const [isPreloading, setIsPreloading] = useState(false);
  const [forceMenuOpen, setForceMenuOpen] = useState(false);
  const requestGenerationRef = useRef(0);
  const isEnabled = Boolean(loadAllOptions);

  useEffect(() => {
    requestGenerationRef.current += 1;
    setPreloadedOptions(null);
    setIsPreloading(false);
    setForceMenuOpen(false);

    return () => {
      requestGenerationRef.current += 1;
    };
  }, [disabled, isEnabled, resetKey]);

  const handlePreload = useCallback(async () => {
    if (!loadAllOptions || disabled || isPreloading) {
      return false;
    }

    if (preloadedOptions !== null) {
      return true;
    }

    const requestGeneration = ++requestGenerationRef.current;
    setIsPreloading(true);
    try {
      const options = await loadAllOptions();
      if (requestGeneration !== requestGenerationRef.current) {
        return false;
      }

      setPreloadedOptions(options);
      return true;
    } catch (error) {
      if (requestGeneration === requestGenerationRef.current) {
        onLoadError?.(error);
      }
      return false;
    } finally {
      if (requestGeneration === requestGenerationRef.current) {
        setIsPreloading(false);
      }
    }
  }, [disabled, isPreloading, loadAllOptions, onLoadError, preloadedOptions]);

  const openMenu = useCallback(() => {
    setForceMenuOpen(true);
  }, []);

  const onMenuClose = useCallback(() => {
    setForceMenuOpen(false);
  }, []);

  return {
    defaultOptions: isEnabled
      ? (preloadedOptions ?? false)
      : fallbackDefaultOptions,
    forceMenuOpen,
    handlePreload,
    hasPreloadedOptions: preloadedOptions !== null,
    isPreloading,
    preloadedOptions,
    onMenuClose,
    openMenu,
  };
}
